import { Toaster, toast as hotToast } from 'react-hot-toast'
import { CheckCircle2, XCircle, Info, AlertTriangle, Loader2 } from 'lucide-react'

/** Mount uma única vez no root da app. */
export function ToastHost() {
  return (
    <Toaster
      position="top-right"
      gutter={10}
      toastOptions={{
        duration: 4500,
        style: {
          fontSize: '13px',
          maxWidth: 460,
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    />
  )
}

// =============================================================================
// Regra global: todo toast tem TÍTULO + DESCRIÇÃO. Quando o caller chama apenas
// `toast.error('msg')` (forma curta), aplicamos um título padrão por variante
// para garantir que ambos sempre apareçam.
// =============================================================================
type Variant = 'success' | 'error' | 'warning' | 'info' | 'loading'

type ToastArg =
  | string
  | {
      title?: string
      description: string
      duration?: number
    }

const DEFAULT_TITLES: Record<Variant, string> = {
  success: 'Tudo certo',
  error:   'Algo deu errado',
  warning: 'Atenção',
  info:    'Aviso',
  loading: 'Processando',
}

const PALETTE: Record<Variant, { bg: string; bar: string; Icon: typeof CheckCircle2; spin?: boolean }> = {
  success: { bg: 'bg-emerald-600', bar: 'bg-emerald-800', Icon: CheckCircle2 },
  error:   { bg: 'bg-rose-600',    bar: 'bg-rose-800',    Icon: XCircle },
  warning: { bg: 'bg-amber-500',   bar: 'bg-amber-700',   Icon: AlertTriangle },
  info:    { bg: 'bg-brand-600',   bar: 'bg-brand-800',   Icon: Info },
  loading: { bg: 'bg-slate-700',   bar: 'bg-slate-900',   Icon: Loader2, spin: true },
}

function normalize(arg: ToastArg, variant: Variant) {
  if (typeof arg === 'string') {
    return { title: DEFAULT_TITLES[variant], description: arg, duration: undefined as number | undefined }
  }
  return {
    title: arg.title ?? DEFAULT_TITLES[variant],
    description: arg.description,
    duration: arg.duration,
  }
}

function render(variant: Variant, arg: ToastArg) {
  const { title, description, duration } = normalize(arg, variant)
  const p = PALETTE[variant]
  const Icon = p.Icon
  // Loading não fecha sozinho — fica até `toast.dismiss(id)` ou `toast.success(...)`
  // que substitui o toast com o id devolvido.
  const isLoading = variant === 'loading'
  return hotToast.custom(
    (t) => (
      <div
        className={[
          'pointer-events-auto flex items-stretch min-w-[320px] max-w-[460px] rounded-md overflow-hidden text-white',
          p.bg,
          t.visible ? 'animate-in slide-in-from-right-4 duration-200' : 'opacity-0',
        ].join(' ')}
      >
        <div className={['w-1.5 shrink-0', p.bar].join(' ')} aria-hidden />
        <div className="flex items-start gap-3 px-4 py-3 flex-1">
          <Icon
            size={20}
            className={['mt-0.5 shrink-0 text-white', p.spin ? 'animate-spin' : ''].join(' ').trim()}
            strokeWidth={2.4}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold leading-tight">{title}</div>
            <div className="text-[12.5px] text-white/95 leading-snug whitespace-pre-wrap break-words mt-0.5">
              {description}
            </div>
          </div>
          {!isLoading && (
            <button
              type="button"
              aria-label="Fechar"
              onClick={() => hotToast.dismiss(t.id)}
              className="cursor-pointer text-white/70 hover:text-white transition shrink-0 -mr-1 leading-none text-lg"
            >
              ×
            </button>
          )}
        </div>
      </div>
    ),
    {
      // Loading: Infinity (não fecha automaticamente)
      duration: isLoading
        ? Infinity
        : (duration ?? (variant === 'error' ? 6500 : 4500)),
    },
  )
}

export const toast = {
  success: (arg: ToastArg) => render('success', arg),
  error:   (arg: ToastArg) => render('error',   arg),
  warning: (arg: ToastArg) => render('warning', arg),
  info:    (arg: ToastArg) => render('info',    arg),
  /**
   * Toast persistente com spinner. Retorna o `id` para uso com `toast.dismiss(id)`
   * quando a operação terminar.
   *
   *   const id = toast.loading({ title: 'Buscando', description: 'Aguarde.' })
   *   try { await doWork() } finally { toast.dismiss(id) }
   */
  loading: (arg: ToastArg) => render('loading', arg),
  dismiss: (id?: string) => hotToast.dismiss(id),
}
